import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getMIPInvestorDetailsData } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext.ts';
import { useMipManageInvestorDetailBreadcrumbs } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/utils/mipManageBreadcrumbs.utils.ts';
import { investorDetailsPath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useNavigateToMessages } from '@dealstack/frontend/app/pages/Project/MIP/Messages/hooks/useNavigateToMessages.ts';
import { useManageInvestorsController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/useManageInvestorsController.tsx';
import { useDetailsPagination } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/hooks/useDetailsPagination.ts';

export const useInvestorDetailsController = () => {
	const { dealId, mipId, investorId } = useRequiredParams('dealId', 'mipId', 'investorId');
	const tMipManage = useTranslationMipManage();
	const { mipData } = useMIPManageContext();
	const { navigateToMessages } = useNavigateToMessages(investorId);
	const openMessages = useCallback(() => navigateToMessages(), [navigateToMessages]);

	const [isDetailsColumnCollapsed, setIsDetailsColumnCollapsed] = useState(false);
	const toggleDetailsColumn = useCallback(
		() => setIsDetailsColumnCollapsed((collapsed) => !collapsed),
		[]
	);

	const investorQuery = useQuery({
		queryKey: [API_PATHS.MIP_INVESTOR_GET_INVESTOR_BY_ID_ID_GET, { investorId }],
		queryFn: ({ signal }) =>
			getMIPInvestorDetailsData(
				API_PATHS.MIP_INVESTOR_GET_INVESTOR_BY_ID_ID_GET,
				{ investorId },
				signal
			),
	});

	useMipManageInvestorDetailBreadcrumbs({
		dealId,
		mipId,
		programmeName: mipData?.programmeName ?? '',
		leafName: tMipManage('investors.breadcrumbs.investor_details'),
	});

	const refreshInvestor = useCallback(() => {
		void investorQuery.refetch();
	}, [investorQuery]);

	const { orderedInvestorIds } = useManageInvestorsController(false);

	const getInvestorPath = useCallback(
		(id: string) =>
			investorDetailsPath({
				dealId,
				mipId,
				investorId: id,
			}),
		[dealId, mipId]
	);

	const pagination = useDetailsPagination({
		ids: orderedInvestorIds,
		currentId: investorId,
		getPath: getInvestorPath,
	});

	return {
		pagination,
		refreshInvestor,
		dealId,
		mipId,
		investorId,
		investor: investorQuery.data?.payload,
		isLoading: investorQuery.isLoading,
		isError: investorQuery.isError,
		isDetailsColumnCollapsed,
		toggleDetailsColumn,
		openMessages,
	};
};
